import { useCallback, useEffect, useState } from 'react';
import SelectComponent from 'components/Inputs/SelectComponent';
import { useDebounce } from 'hooks/useDebounce';
import { strToBase64 } from 'utils/convertString';
import { Obj } from 'types/common';
import { AutocompleteSelectProps } from './types';

function AutocompleteSelect<T extends Obj>({
  data,
  onSearch,
  base64Converting = false,
  filterArray,
  name,
  optionLabel = 'name',
  optionValue = 'id',
  ...selectProps
}: AutocompleteSelectProps<T>) {
  const [options, setOptions] = useState<T[]>([]);

  useEffect(() => {
    setOptions(data || []);
  }, [data]);

  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const debouncedVaue = useDebounce(searchValue);

  useEffect(() => {
    search(debouncedVaue);
  }, [debouncedVaue]);

  const search = useCallback(
    async (search: string) => {
      if (search) {
        if (onSearch) {
          setIsLoading(true);
          const searched = await onSearch(base64Converting ? strToBase64(search) : search);
          if (searched) {
            if (filterArray) {
              const filtred = searched?.filter(({ id }) => !filterArray.some((item) => item.id === id));
              setOptions(filtred);
            } else setOptions(searched);
          }

          setIsLoading(false);
        } else {
          const filtred = data?.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
          setOptions(filtred || []);
        }
      } else {
        // set defalul options or nothing
        setOptions(data || []);
      }
    },
    [data, onSearch]
  );

  return (
    <SelectComponent<T>
      {...selectProps}
      name={name}
      options={options as T[]}
      onInputChange={setSearchValue}
      isLoading={isLoading}
      isSearchSelect
      optionLabel={optionLabel}
      optionValue={optionValue}
    />
  );
}

export default AutocompleteSelect;
